import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, useRoutes } from 'react-router-dom';

import { getLocationReferral } from '@app/shared/lib/get-location-referral';
import { useSchool } from '@app/shared/lib/hooks';
import { useUser } from '@app/shared/lib/hooks/useUser';
import { LoadingOverlay } from '@app/shared/ui';
import { BasicLayout } from '@app/shared/ui/BasicLayout/BasicLayout';
import { LearnLayout } from '@app/shared/ui/LearnLayout';

const AuthLayout = lazy(() => import('./auth'));
const Login = lazy(() => import('./login'));
const LoginToken = lazy(() => import('./login-token'));
const LoginAs = lazy(() => import('./login-as'));
const SchoolMaterialsPage = lazy(() => import('./school-materials'));
const AccessPage = lazy(() => import('./access'));
const PartnershipPage = lazy(() => import('./partnership'));
const BillingPage = lazy(() => import('./billing'));
const RegisterPage = lazy(() => import('./register'));
const AuthContactPage = lazy(() => import('./auth-contact'));
const ForgotPassword = lazy(() => import('./forgot-password'));
const ResetPassword = lazy(() => import('./reset-password-token'));
const ConfirmEmail = lazy(() => import('./confirm-email'));
const ConfirmInvite = lazy(() => import('./confirm-invite'));
const UnsubscribeEmails = lazy(() => import('./email-unsubscribe'));
const UnsubscribeEmailNotifications = lazy(() => import('./email-notification-unsubscribe'));
const LearnPage = lazy(() => import('./learn'));
const PreviewPage = lazy(() => import('./preview'));
const StudentPreviewPage = lazy(() => import('./student-preview'));
const GamificationPage = lazy(() => import('./gamification'));
const LearnLibraryPage = lazy(() => import('./library'));
const ForumPage = lazy(() => import('./forum'));

const BasicLayoutRouting: React.FC = () => {
    const user = useUser();
    const school = useSchool();

    // FIXME: use isAuthenticated, jist verify token is present in sdk
    if (!user.isAuthorized) {
        return <Navigate to={`/login?returnUrl=${getLocationReferral()}`} />;
    }

    // migration lock
    const hasMigrationLock = school.hasMigrationLock ?? false;
    if (hasMigrationLock) {
        if (window.location.pathname !== '/') return <Navigate to="/" />;
    } else if (school.isLocked && !window.location.pathname.startsWith('/locked')) return <Navigate to="/locked" />; // billing lock

    return (
        <Suspense fallback={<LoadingOverlay />}>
            <BasicLayout />
        </Suspense>
    );
};

const Router = () => {
    const user = useUser();
    // We removed the <BrowserRouter> element from App because the
    // useRoutes hook needs to be in the context of a <BrowserRouter>
    // element. This is a common pattern with React Router apps that
    // are rendered in different environments. To render an <App>,
    // you'll need to wrap it in your own <BrowserRouter> element.
    const element = useRoutes([
        {
            path: '/',
            element: <BasicLayoutRouting />,
            children: [
                {
                    path: '/',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <SchoolMaterialsPage />
                        </Suspense>
                    )
                },
                { path: '/courses', element: <Navigate to="/" /> },
                {
                    path: '/access',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <AccessPage />
                        </Suspense>
                    )
                },
                {
                    path: '/partnership',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <PartnershipPage />
                        </Suspense>
                    )
                },
                {
                    path: '/gamification',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <GamificationPage />
                        </Suspense>
                    )
                },
                {
                    path: '/forum',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ForumPage />
                        </Suspense>
                    )
                },
                {
                    path: '/forum/:threadId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ForumPage />
                        </Suspense>
                    )
                },
                {
                    path: '/forum/:threadId/:topicId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ForumPage />
                        </Suspense>
                    )
                },
                {
                    path: '/locked',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <BillingPage />
                        </Suspense>
                    )
                }
            ]
        },
        {
            path: '/',
            element: (
                <Suspense fallback={<LoadingOverlay />}>
                    <AuthLayout />
                </Suspense>
            ),
            children: [
                {
                    path: '/login',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <Login />
                        </Suspense>
                    )
                },
                {
                    path: '/login-token/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LoginToken />
                        </Suspense>
                    )
                },
                {
                    path: '/login-as/:sessionId/:studentId/:timestamp/:signature',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LoginAs />
                        </Suspense>
                    )
                },
                {
                    path: '/register',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <RegisterPage />
                        </Suspense>
                    )
                },
                {
                    path: '/c/:contactId/:inviteToken?',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <AuthContactPage />
                        </Suspense>
                    )
                },
                {
                    path: '/forgot-password',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ForgotPassword />
                        </Suspense>
                    )
                },
                {
                    path: '/reset-password/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ResetPassword />
                        </Suspense>
                    )
                },
                {
                    path: '/confirm-email/:userId/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ConfirmEmail />
                        </Suspense>
                    )
                },
                {
                    path: '/confirm-invite/:userId/:token',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <ConfirmInvite />
                        </Suspense>
                    )
                },
                {
                    path: '/email/unsubscribe',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <UnsubscribeEmails />
                        </Suspense>
                    )
                },
                {
                    path: '/email-notification/unsubscribe',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <UnsubscribeEmailNotifications />
                        </Suspense>
                    )
                }
            ]
        },
        {
            path: '/',
            // FIXME: надо isAuthenticated
            element:
                user.user !== null ? (
                    <Suspense fallback={<LoadingOverlay />}>
                        <LearnLayout />
                    </Suspense>
                ) : (
                    <Navigate to={`/login?returnUrl=${getLocationReferral()}`} />
                ),
            children: [
                {
                    path: '/learn/:courseItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnPage />
                        </Suspense>
                    )
                },
                {
                    path: '/learn/:courseItemId/:tab',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnPage />
                        </Suspense>
                    )
                },
                {
                    path: '/course-preview/:courseId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <PreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/course-preview/:courseId/:courseItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <PreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/course-preview/:courseId/:courseItemId/:tab',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <PreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/student-course-preview/:studentCourseId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <StudentPreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/student-course-preview/:studentCourseId/:courseItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <StudentPreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/student-course-preview/:studentCourseId/:courseItemId/:tab',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <StudentPreviewPage />
                        </Suspense>
                    )
                },
                {
                    path: '/library/:libraryId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnLibraryPage />
                        </Suspense>
                    )
                },
                {
                    path: '/library/:libraryId/:libraryItemId',
                    element: (
                        <Suspense fallback={<LoadingOverlay />}>
                            <LearnLibraryPage />
                        </Suspense>
                    )
                }
            ]
        },
        {
            path: '*',
            element: <Navigate to="/" />
        }
    ]);

    return element;
};

export const Pages: React.FC = () => (
    <BrowserRouter>
        <Router />
    </BrowserRouter>
);
